import { compact, sumBy, partition, findLast } from 'lodash';
import { hToMs } from '../../lib/time';
import { get, mappedMax, assertedDefined, isDefined } from '../../lib/util';
import type { ViolationIndicatorBasis } from '../../state/planning/options';
import type { OrderViolationLimits } from '../ScenarioImprovementParams';
import type { ClientId } from './ClientId';
import type {
  ScheduleData,
  ScheduleOptimizationOpts,
  ComputeScheduleMetricsParams,
} from './Core';
import {
  countTotalIdleTime,
  getMaxLDViolations,
  countTotalConstrainedDelayByOrder,
  DELAY_CONSTRAINT_HOURS,
} from './Core';
import {
  countTotalMassDistance,
  calculateCostPerTonMile,
} from './Core.voyage-metrics';
import { DeriveCapacitiesAndTimes } from './DeriveCapacitiesAndTimes';
import type {
  BestSchedule,
  OrdersToVesselCombination,
} from './suggestions/Suggestions';
import type { TimeData } from './TimeData';
import {
  isCargoEventTimeData,
  isLoadEventTimeData,
  getEventEndLocationId,
  getEventStartLocationId,
  isSingleEventTimeData,
} from './TimeData';
import { Objective } from './suggestions/VoyageSuggestionParams';
import { getPortDraftMassCap } from '../timeline/utils/VesselDraftUtils';
import { stringToNumOpt } from './util';

/** Float slack for treating a derived load ratio as below its band-minimum ratio. */
const BELOW_MIN_RATIO_EPSILON = 1e-6;

/**
 * Whether a load event's derived amount fell below its band minimum on the binding dimension. DCAT
 * loads one ratio across both dimensions, so the loaded ratio is recovered from whichever dimension
 * the order uses (mass, or volume for a mass-less cargo); a cargo with neither band cannot be below
 * minimum.
 */
function isLoadBelowBandMinimum(td: TimeData): boolean {
  if (!isLoadEventTimeData(td)) return false;
  const { cargoRanges } = td.event.order;
  const maxMass = cargoRanges.mass.max;
  const maxVolume = cargoRanges.volume.max;
  const massFloorRatio = maxMass > 0 ? cargoRanges.mass.min / maxMass : null;
  const volumeFloorRatio =
    maxVolume > 0 ? cargoRanges.volume.min / maxVolume : null;
  if (massFloorRatio === null && volumeFloorRatio === null) return false;
  const floorRatio = Math.max(massFloorRatio ?? 0, volumeFloorRatio ?? 0);
  const loadedRatio =
    maxMass > 0 ? td.mass / maxMass : maxVolume > 0 ? td.volume / maxVolume : 1;
  return loadedRatio < floorRatio - BELOW_MIN_RATIO_EPSILON;
}

/**
 * Finds the first event index at the given location, walking backward from startIndex.
 * @param startIndex expected to be a valid index in timeData
 * Returns the index of the first event at this location (port call arrival).
 */
const findFirstEventAtLocation = (
  timeData: readonly TimeData[],
  startIndex: number,
): TimeData => {
  const locationId = getEventStartLocationId(timeData[startIndex].event);
  let firstIndex = startIndex;
  for (let i = startIndex - 1; i >= 0; i--) {
    if (getEventStartLocationId(timeData[i].event) !== locationId) break;
    firstIndex = i;
  }
  return timeData[firstIndex];
};

/**
 * Finds the index of the first event after the given location, walking forward from startIndex.
 * Returns the index past all events at this location (first event after port call departure).
 */
const findFirstEventAfterLocation = (
  timeData: readonly TimeData[],
  startIndex: number,
): TimeData | null => {
  const locationId = getEventStartLocationId(timeData[startIndex].event);
  let afterIndex = startIndex + 1;
  while (
    afterIndex < timeData.length &&
    getEventStartLocationId(timeData[afterIndex].event) === locationId
  ) {
    afterIndex++;
  }
  return timeData.at(afterIndex) ?? null;
};

/**
 * Checks if there are existing (non-shipment) cargo or single events at the same location
 * by walking adjacent events. A global search would give false positives
 * when the vessel visits the same port twice.
 */
const hasExistingEventsAtSameLocation = (
  timeData: readonly TimeData[],
  tdBelongsToAssignedShipment: (td: TimeData) => boolean,
  centerIndex: number,
): boolean => {
  const locationId = getEventStartLocationId(timeData[centerIndex].event);
  // Walk backward from centerIndex
  for (let i = centerIndex - 1; i >= 0; i--) {
    if (getEventStartLocationId(timeData[i].event) !== locationId) break;
    if (
      (isCargoEventTimeData(timeData[i]) &&
        !tdBelongsToAssignedShipment(timeData[i])) ||
      isSingleEventTimeData(timeData[i])
    )
      return true;
  }
  // Walk forward from centerIndex
  for (let i = centerIndex + 1; i < timeData.length; i++) {
    if (getEventStartLocationId(timeData[i].event) !== locationId) break;
    if (
      (isCargoEventTimeData(timeData[i]) &&
        !tdBelongsToAssignedShipment(timeData[i])) ||
      isSingleEventTimeData(timeData[i])
    )
      return true;
  }
  return false;
};

/**
 * Checks ballast distance violations for entry and exit legs of shipment assignments.
 * Returns the maximum ballast distance violation if any, undefined otherwise.
 */
const checkBallastViolations = (
  timeData: readonly TimeData[],
  tdBelongsToAssignedShipment: (td: TimeData) => boolean,
  firstShipmentTdIndex: number,
  maxBallastNm: number,
): number | undefined => {
  let maxViolation: number | undefined;

  const checkBallast = (td: TimeData) => {
    const ballast = !td.laden ? td.distance : 0;
    if (ballast > maxBallastNm) {
      maxViolation = Math.max(maxViolation ?? 0, ballast);
    }
  };

  // 1. Entry ballast: check the arrival leg to the first shipment port call
  if (firstShipmentTdIndex >= 0) {
    const shipmentBeginsInExistingPortCall = hasExistingEventsAtSameLocation(
      timeData,
      tdBelongsToAssignedShipment,
      firstShipmentTdIndex,
    );

    if (!shipmentBeginsInExistingPortCall) {
      const arrival = findFirstEventAtLocation(timeData, firstShipmentTdIndex);
      checkBallast(arrival);
    }
  }

  // 2. Exit ballast: check the departure leg from the last shipment port call
  const lastShipmentTdIndex = timeData.findLastIndex(
    tdBelongsToAssignedShipment,
  );
  if (lastShipmentTdIndex >= 0) {
    const shipmentEndsInExistingPortCall = hasExistingEventsAtSameLocation(
      timeData,
      tdBelongsToAssignedShipment,
      lastShipmentTdIndex,
    );

    if (!shipmentEndsInExistingPortCall) {
      const exit = findFirstEventAfterLocation(timeData, lastShipmentTdIndex);
      // Check the first event after the last shipment port call
      if (exit) {
        checkBallast(exit);
      }
    }
  }

  return maxViolation;
};

export type ScheduleMetrics = {
  mass: number;
  shipmentAmount: number;
  voyageDurationH: number;
  totalDelayH: number;
  objectiveScore: number;
  /** Legs of this candidate whose route variant has no resolved distance. */
  unresolvedLegs: number;
  resultValue: BestSchedule;
  maxViolationMs: OrderViolationLimits;
  /** Empty object when no violation exceeds an acceptable level. */
  violations: {
    newAssignmentHasExclusionViolation?: boolean;
    maxEarlyMs?: number;
    maxLateMs?: number;
    totalDelayH?: number;
    maxBallastDistanceNm?: number;
    draftViolation?: boolean;
    loadedBelowMinimum?: boolean;
  };
};

export function hasMetricsViolations(metrics: ScheduleMetrics): boolean {
  return Object.keys(metrics.violations).length > 0;
}

/** Returns null if the schedule has too high violations relative to opts given */
export function computeScheduleMetrics(
  combination: OrdersToVesselCombination,
  /** Given when comparing to an earlier state. Affects how delays are considered. */
  originalResults: null | ScheduleData,
  deriveParams: ComputeScheduleMetricsParams,
  opts: ScheduleOptimizationOpts,
  allAvailableOrderIds: Set<number>,
  violationBasis: ViolationIndicatorBasis,
): ScheduleMetrics {
  const { schedule, assignments } = combination;
  const { timeData, totals, inputs } = DeriveCapacitiesAndTimes(
    deriveParams.vessel,
    schedule,
    deriveParams.context,
    deriveParams.config,
    { skipSwapComputation: true, calculateCosts: true },
    deriveParams.now,
  );
  const violations: ScheduleMetrics['violations'] = {};
  const originalHoursAtSea = originalResults?.totals.hoursAtSea ?? 0;
  const combinationHoursAtSea = totals.hoursAtSea;
  const { cost, tcCost, distance, ballastDistance, mass, unresolvedLegs } =
    totals;
  const totalIdle = countTotalIdleTime(timeData, opts.countIdleFromIndex ?? 0);

  const assignedShipmentIds = new Set<ClientId>(
    compact(assignments.map((a) => a.order.shipment?.clientId)),
  );

  const tdBelongsToAssignedShipment = (td: TimeData) =>
    isCargoEventTimeData(td) &&
    ((td.event.order.shipment &&
      assignedShipmentIds.has(td.event.order.shipment.clientId)) ||
      allAvailableOrderIds.has(td.event.order.id));

  const shipmentTds = timeData.filter(tdBelongsToAssignedShipment);

  if (opts.respectCargoExclusions) {
    const newAssignmentHasExclusionViolation = shipmentTds.some(
      (td) => td.assortmentExclusions.length > 0,
    );
    if (newAssignmentHasExclusionViolation) {
      violations.newAssignmentHasExclusionViolation = true;
    }
  }

  // Check if any of the new events start too early or late.
  const maxViolationMs = getMaxLDViolations(shipmentTds, violationBasis);
  if (maxViolationMs.load.early > (opts.violationLimitsMs?.load.early ?? 0)) {
    violations.maxEarlyMs = maxViolationMs.load.early;
  }
  if (
    maxViolationMs.discharge.early >
    (opts.violationLimitsMs?.discharge.early ?? 0)
  ) {
    violations.maxEarlyMs = Math.max(
      violations.maxEarlyMs ?? 0,
      maxViolationMs.discharge.early,
    );
  }
  if (maxViolationMs.load.late > (opts.violationLimitsMs?.load.late ?? 0)) {
    violations.maxLateMs = maxViolationMs.load.late;
  }
  if (
    maxViolationMs.discharge.late >
    (opts.violationLimitsMs?.discharge.late ?? 0)
  ) {
    violations.maxLateMs = Math.max(
      violations.maxLateMs ?? 0,
      maxViolationMs.discharge.late,
    );
  }

  const loadTimeDatas = shipmentTds.filter((td) => isLoadEventTimeData(td));

  const shipmentAmount = sumBy(loadTimeDatas, (ltd) => ltd.amount);

  const [shipmentOrderTds, nonShipmentOrderTds] = partition(timeData, (td) =>
    tdBelongsToAssignedShipment(td),
  );
  const firstShipmentTdIndex = timeData.findIndex(tdBelongsToAssignedShipment);
  const firstShipmentTd = get(timeData, firstShipmentTdIndex);
  const tdPrecedingShipment = get(timeData, firstShipmentTdIndex - 1);
  const locationPrecedingShipment =
    tdPrecedingShipment?.event &&
    getEventEndLocationId(tdPrecedingShipment.event);
  const shipmentBeginsInExistingPortCall =
    firstShipmentTd &&
    getEventStartLocationId(firstShipmentTd.event) ===
      locationPrecedingShipment &&
    hasExistingEventsAtSameLocation(
      timeData,
      tdBelongsToAssignedShipment,
      firstShipmentTdIndex,
    );

  /** Delays of all orders in the whole schedule */
  const delayByNonShipmentOrderId =
    countTotalConstrainedDelayByOrder(nonShipmentOrderTds);

  const originalDelayByOrderId =
    originalResults?.timeData &&
    countTotalConstrainedDelayByOrder(originalResults.timeData);

  /** How much worse existing delays became */
  const nonShipmentDelayMaxDeltaH =
    originalDelayByOrderId &&
    (delayByNonShipmentOrderId.size === 0
      ? 0
      : mappedMax(
          delayByNonShipmentOrderId.entries(),
          ([orderId, delay]) =>
            delay - (originalDelayByOrderId.get(orderId) ?? 0),
        ));

  const delayByShipmentOrderId =
    countTotalConstrainedDelayByOrder(shipmentOrderTds);

  // TODO: Improvement: In suggestions generation this rule currently prevents ever generating a long round-trip in between existing events.
  // This is because temporarily the ballast will be large until the return trip is also added.
  // The algorithm will need to somehow allow temporary violations when they can be fixed by later order additions.
  if (opts.maxBallastDistanceNm) {
    const ballastViolation = checkBallastViolations(
      timeData,
      tdBelongsToAssignedShipment,
      firstShipmentTdIndex,
      opts.maxBallastDistanceNm,
    );
    if (ballastViolation !== undefined) {
      violations.maxBallastDistanceNm = ballastViolation;
    }
  }

  const lastShipmentTdIndex = timeData.findLastIndex(
    tdBelongsToAssignedShipment,
  );

  // Invalid-routing draft gate: a port whose max draft is below the vessel's empty draft cannot be
  // entered at any load, so it is the only draft case exclude mode filters out. A satisfiable-but-tight
  // port only caps the loaded amount and is never excluded. Load-independent — the cap is a property of
  // the vessel and the port, not of how much cargo happens to be aboard.
  if (opts.respectDraftRestrictions) {
    for (
      let i = Math.max(0, firstShipmentTdIndex);
      i <= lastShipmentTdIndex;
      i++
    ) {
      const td = timeData[i];
      const location = 'location' in td.event ? td.event.location : null;
      const portMaxDraft = stringToNumOpt(location?.draft);
      if (!isDefined(portMaxDraft)) continue;
      if (getPortDraftMassCap(deriveParams.vessel, portMaxDraft) === 0) {
        violations.draftViolation = true;
        break;
      }
    }
  }

  // Suggestions treat a below-band-minimum load as infeasibility: the gate rejects a candidate whose
  // forward derive had to spill a load below floor to fit the schedule, catching sub-minimum loads the
  // fit-at-minimum admission cannot see (allocation and co-load reservation cut at derive time). The
  // manual-assignment overload retry sets allowLoadBelowMinimum, where a physically-capped over-capacity
  // load is the intended result rather than infeasibility, so the gate is skipped there.
  if (!opts.allowLoadBelowMinimum) {
    for (
      let i = Math.max(0, firstShipmentTdIndex);
      i <= lastShipmentTdIndex;
      i++
    ) {
      if (isLoadBelowBandMinimum(timeData[i])) {
        violations.loadedBelowMinimum = true;
        break;
      }
    }
  }

  /** The max delay within the shipment */
  const shipmentDelayMaxH = Math.max(
    0,
    mappedMax(delayByShipmentOrderId.entries(), ([orderId, delay]) => {
      /** Special case: when added such that the shipment begins at an existing location, only start load delay from load start instead of its eta (if it's better than vs eta).
       * https://seaber2.myjetbrains.com/youtrack/issue/SEABER-4087 */
      if (
        shipmentBeginsInExistingPortCall &&
        firstShipmentTd &&
        isCargoEventTimeData(firstShipmentTd) &&
        orderId === firstShipmentTd.event.order.id
      ) {
        const loadTd = firstShipmentTd;
        const dischargeTd = assertedDefined(
          findLast(
            shipmentTds,
            (td) => isCargoEventTimeData(td) && td.event.order.id === orderId,
          ),
          'Order should have discharge',
        );
        return (
          Math.min(
            DELAY_CONSTRAINT_HOURS,
            Math.abs(loadTd.cancellingOrDeliveryViolationVsEta),
            Math.abs(loadTd.cancellingOrDeliveryViolationVsOperations),
            Math.abs(loadTd.cancellingOrDeliveryViolationVsEvent),
          ) +
          Math.min(
            DELAY_CONSTRAINT_HOURS,
            Math.abs(dischargeTd.cancellingOrDeliveryViolationVsEta),
          )
        );
      } else {
        return delay;
      }
    }),
  );

  /** A measure balancing both delays: existing schedule and the current shipment to assign to */
  const totalDelayH = (nonShipmentDelayMaxDeltaH ?? 0) + shipmentDelayMaxH;
  if (
    opts.maximumDelayDeltaMs &&
    hToMs(totalDelayH) > opts.maximumDelayDeltaMs
  ) {
    violations.totalDelayH = totalDelayH;
  }

  const totalMassDistance = countTotalMassDistance(
    timeData,
    deriveParams.context.distances,
  );
  const vcp = totalMassDistance / distance;

  const voyageDurationH = combinationHoursAtSea;

  const voyageDurationDeltaH = combinationHoursAtSea - originalHoursAtSea;

  const objective = opts.objective;

  // Less is better. An unresolved leg is not scored out here: it drops out of the
  // distance every objective derives from, so `compareScheduleRanking` demotes the
  // candidate on `unresolvedLegs` instead of on this number.
  const objectiveScore =
    objective === Objective.MaximizeTCE
      ? cost - tcCost
      : objective === Objective.MinimizeCost
        ? cost
        : objective === Objective.MaximizeVCP
          ? 1 / vcp
          : objective === Objective.ShortestDistance
            ? voyageDurationDeltaH
            : objective === Objective.MinimizeCostPerTonMile
              ? calculateCostPerTonMile(cost, totalMassDistance)
              : ballastDistance;

  return {
    mass,
    shipmentAmount,
    voyageDurationH,
    totalDelayH,
    objectiveScore,
    unresolvedLegs,
    maxViolationMs,
    resultValue: {
      combination,
      timeData,
      totals,
      inputs,
      amount: shipmentAmount,
      delay: totalDelayH,
      idle: totalIdle,
      cost,
      objectiveScore,
      unresolvedLegs,
    },
    violations,
  };
}
